import { useRef, useCallback, useEffect, useMemo } from 'react';
import {
  ANCHOR_SIZES,
  ICON_SIZES,
  MARKER_SVGS,
  ZINDEXES
} from '../../../map-search-v9/lib/markers';

/**
 * Generates a unique hash key for a given location and provider
 * @param {Number} locationId
 * @param {Number} providerId
 * @returns {String} - The generated hash key
 */
const hashKey = (locationId, providerId) => {
  return `${locationId}_${providerId}`;
};

/**
 * Hook to handle provider highlighting on map markers with keyboard navigation support
 * Works with custom marker components to provide dynamic highlighting
 *
 * @param {Array} providers - Array of provider objects
 * @param {string|null} hoveredProviderId - Currently hovered provider ID
 *
 * @returns {Object} - Highlighting functions and marker management
 */
export const useMapHighlighting = (providers, hoveredProviderId) => {
  const markersRef = useRef([]);

  const providerLocationMap = useMemo(() => {
    const map = new Map();
    if (providers && Array.isArray(providers)) {
      providers.forEach((provider) => {
        if (provider.locations) {
          provider.locations.forEach((location) => {
            map.set(hashKey(location.id, provider.id), provider.id);
          });
        }
      });
    }
    return map;
  }, [providers]);

  const highlightGoogleMapsMarkers = useCallback(
    (providerId) => {
      if (!window || !window.google) return;

      markersRef.current.forEach(({ marker, meta }) => {
        const markerProviderId = providerLocationMap.get(
          hashKey(meta.location.id, providerId)
        );

        if (marker && marker.setIcon) {
          const isHighlighted = providerId && markerProviderId === providerId;

          // Use pre-encoded SVG data URLs
          const dataUrl = isHighlighted
            ? MARKER_SVGS.FOCUSED
            : MARKER_SVGS.UNFOCUSED;

          const iconConfig = {
            url: dataUrl,
            scaledSize: new window.google.maps.Size(
              isHighlighted
                ? ICON_SIZES.FOCUSED.width
                : ICON_SIZES.UNFOCUSED.width,
              isHighlighted
                ? ICON_SIZES.FOCUSED.height
                : ICON_SIZES.UNFOCUSED.height
            ),
            anchor: new window.google.maps.Point(
              isHighlighted
                ? ANCHOR_SIZES.FOCUSED.width
                : ANCHOR_SIZES.UNFOCUSED.width,
              isHighlighted
                ? ANCHOR_SIZES.FOCUSED.height
                : ANCHOR_SIZES.UNFOCUSED.height
            )
          };

          marker.setIcon(iconConfig);
          marker.setZIndex(
            isHighlighted ? ZINDEXES.FOCUSED : ZINDEXES.UNFOCUSED
          );
        }
      });
    },
    [providerLocationMap]
  );

  const highlightAwsMapMarkers = useCallback(
    (providerId) => {
      markersRef.current.forEach(({ marker, meta }) => {
        const markerProviderId = providerLocationMap.get(
          hashKey(meta.location.id, providerId)
        );

        if (marker && marker.getElement) {
          const isHighlighted = providerId && markerProviderId === providerId;
          const markerElement = marker.getElement();

          if (markerElement) {
            const dataUrl = isHighlighted
              ? MARKER_SVGS.FOCUSED
              : MARKER_SVGS.UNFOCUSED;

            const width = isHighlighted
              ? ICON_SIZES.FOCUSED.width
              : ICON_SIZES.UNFOCUSED.width;
            const height = isHighlighted
              ? ICON_SIZES.FOCUSED.height
              : ICON_SIZES.UNFOCUSED.height;

            markerElement.src = dataUrl;
            markerElement.style.width = `${width}px`;
            markerElement.style.height = `${height}px`;
            markerElement.style.zIndex = isHighlighted
              ? ZINDEXES.FOCUSED
              : ZINDEXES.UNFOCUSED;
          }
        }
      });
    },
    [providerLocationMap]
  );

  const highlightMarkers = useCallback(
    (providerId) => {
      if (!markersRef.current.length || !window) return;

      const firstMarker = markersRef.current[0]?.marker;
      const isGoogleMaps = firstMarker && firstMarker.setIcon && window.google;

      if (isGoogleMaps) {
        highlightGoogleMapsMarkers(providerId);
      } else {
        highlightAwsMapMarkers(providerId);
      }
    },
    [highlightGoogleMapsMarkers, highlightAwsMapMarkers]
  );

  useEffect(() => {
    highlightMarkers(hoveredProviderId);
  }, [hoveredProviderId, highlightMarkers]);

  const setMarkers = (markers) => {
    markersRef.current = markers || [];
  };

  return {
    highlightMarkers,
    setMarkers
  };
};
